/* -------------------------- Design imports start -------------------------- */
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined'
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import ViewInArIcon from '@mui/icons-material/ViewInAr'
import { Grid, Tooltip } from '@mui/material'
import React, { type ReactElement } from "react";
import { useTranslation } from 'react-i18next'

import Button from '../../../components/Button'
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  spacing?: number
  wrapAlign?: 'left' | 'right'
  onShowStlClick: () => void
  onCreateFolderClick: () => void
  onRenameFileClick?: () => void
  onDownloadFileClick: () => void
  onDeleteFileClick: () => void
  allowCreateFolder: boolean
  allowRename: boolean
  allowDownload: boolean
  allowDelete: boolean
  allowShowStl: boolean
  sx?: React.CSSProperties
}

/**
 * Actions that change the Filesystem
 * Actions:
 *  - reload --> After uploading stp files it sometimes dont show the /converted folder
 *               reloading lets the user get all folders
 *  - new --> opens dialog to create a new folder
 *  - download --> download all selected files
 *  - delete --> delete all selected files
 */

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export const FilesystemActions = (props: Props): ReactElement => {
  /* -------------------------- Non state data start -------------------------- */
  const {
    spacing,
    wrapAlign = 'left',
    onShowStlClick,
    onCreateFolderClick,
    onDownloadFileClick,
    onDeleteFileClick,
    allowCreateFolder,
    allowShowStl,
    allowDownload,
    allowDelete,
    sx,
  } = props
  const { t } = useTranslation(['files'])
  /* --------------------------- Non state data end --------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  let actionButtons = [
    /* {
      title: t('reload'),
      tooltip: t('tooltip.reload'),
      onClick: onReloadClick,
      disabled: false,
      icon: <RefreshIcon />,
    }, */
    {
      title: t('common:content.label.showStl'),
      tooltip: t('common:content.label.showStl'),
      onClick: onShowStlClick,
      disabled: !allowShowStl,
      icon: <ViewInArIcon />,
    },
    {
      title: t('interaction.button.new'),
      tooltip: t('content.label.newFolder'),
      onClick: onCreateFolderClick,
      disabled: !allowCreateFolder,
      icon: <CreateNewFolderOutlinedIcon />,
    },
    {
      title: t('content.label.download'),
      tooltip: t('content.label.download'),
      onClick: onDownloadFileClick,
      disabled: !allowDownload,
      icon: <DownloadOutlinedIcon />,
    },
    /*{
      title: t('rename'),
      onClick: onRenameFileClick,
      disabled: !allowRename,
      icon: <EditRoundedIcon />,
    },*/
    {
      title: t('content.label.deleteFiles'),
      tooltip: t('content.label.deleteFiles'),
      onClick: onDeleteFileClick,
      disabled: !allowDelete,
      icon: <DeleteForeverOutlinedIcon />,
    },
  ]
  const direction = wrapAlign === 'right' ? 'rtl' : 'ltr'
  if (wrapAlign === 'right') {
    actionButtons = actionButtons.reverse()
  }

  /* -------------------------- Render constants end -------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <div style={{ ...sx, direction: direction }}>
      <Grid container spacing={spacing}>
        {actionButtons.map((actionButton, index) => {
          return (
            <Grid item key={index}>
              {actionButton.tooltip && !actionButton.disabled ? (
                <Tooltip title={actionButton.tooltip }>
                  <Button
                    id={actionButton.title}
                    onClick={actionButton.onClick}
                    disabled={actionButton.disabled}
                    color="primary"
                    variant="outlined"
                    startIcon={actionButton.icon}
                    sx={{ direction: 'ltr' }}
                  >
                    {actionButton.title}
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  id={actionButton.title}
                  onClick={actionButton.onClick}
                  disabled={actionButton.disabled}
                  variant="outlined"
                  color="primary"
                  startIcon={actionButton.icon}
                  sx={{ direction: 'ltr' }}
                >
                  {actionButton.title}
                </Button>
              )}
            </Grid>
          )
        })}
      </Grid>
    </div>
  )
}
